import isEqual from 'lodash/isEqual';
import { type DependencyList, type EffectCallback, useEffect, useRef } from 'react';

const isPrimitive = (value: unknown): boolean => value !== Object(value);

/**
 * Like React.useEffect, except dependencies are compared by deep equality
 * instead of reference equality. Use when a dep is an object or array that
 * is freshly constructed each render but whose contents change rarely.
 *
 * ⚠️ Do not pass React elements in `deps`. The deep-equality impl walks the
 * element's `_owner` chain, which historically infinite-looped in older
 * deep-compare libraries (see facebook/react#19811). The current impl
 * detects the cycle and returns a result, but the traversal is wasted work.
 * Extract the specific primitive you care about and pass that instead.
 *
 * ⚠️ Functions are compared by reference (not by source). A dep like
 * `[() => x]` is never deep-equal to the previous render's literal, so the
 * effect fires every render — wrap the callback with `useCallback` (or
 * extract a stable upstream handler) before passing it in.
 *
 * Emits dev-only `console.warn` when:
 *   - `deps` is missing or empty (use React.useEffect instead)
 *   - every dep is a primitive value (use React.useEffect instead)
 *
 * @param {EffectCallback} effect - Effect callback (identical contract to React.useEffect)
 * @param {DependencyList} deps - Dependency list compared by deep equality across renders
 * @returns {void}
 */
const useDeepCompareEffect = (effect: EffectCallback, deps: DependencyList): void => {
  if (import.meta.env.DEV) {
    if (!Array.isArray(deps) || deps.length === 0) {
      console.warn('`useDeepCompareEffect` should not be used with no dependencies. Use React.useEffect instead.');
    } else if (deps.every(isPrimitive)) {
      console.warn(
        '`useDeepCompareEffect` should not be used with dependencies that are all primitive values. Use React.useEffect instead.',
      );
    }
  }

  const ref = useRef<DependencyList | undefined>(undefined);

  if (!ref.current || !isEqual(ref.current, deps)) {
    ref.current = deps;
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps tracked manually via deep equality
  useEffect(effect, ref.current);
};

export { useDeepCompareEffect };
